import { AfterViewInit, Component, ElementRef, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { _log } from '@shared/aux_helper_environment';
import { GenieStatementResponse } from 'app/services/agents/databricks-genie-ai-chat.models';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { InsightPriority, InsightType } from './insights-suggestions-mock.data';

interface InsightDetailData {
  id: string;
  title: string;
  description: string;
  date: Date;
  type: InsightType;
  priority: InsightPriority;
  estimatedImpact: number;
  detailedReport: string;
  tableData?: GenieStatementResponse;
  insight_id?: string | number;
}

@Component({
  selector: 'insight-detail-modal',
  template: `
    <div mat-dialog-title class="modal-title-row" style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
      <h2 style="margin: 0; line-height: 1.2;">{{ data.title }}</h2>
      <button
        mat-stroked-button
        color="primary"
        *ngIf="data.insight_id"
        (click)="onViewConversation()"
        [matTooltip]="getViewConversationTooltip()"
        class="view-chat-button"
        style="flex-shrink: 0;"
      >
        <mat-icon>chat</mat-icon>
        <span style="margin-left: 8px;">{{ getViewConversationText() }}</span>
      </button>
    </div>

    <mat-dialog-content class="modal-content">
      <div class="insight-detail-modal">
        <div class="modal-header-info">
          <div class="date-badge">{{ formatDate(data.date) }}</div>
          <span class="type-badge" [attr.data-type]="data.type">{{ getTypeLabel(data.type) }}</span>
          <span class="priority-badge" [attr.data-priority]="data.priority">{{ getPriorityLabel(data.priority) }}</span>

          <div class="impact-badge">
            <span class="impact-label" style="font-size: 12px; color: #fff">Impacto</span>
            <span>{{ formatCurrency(data.estimatedImpact) }}</span>
          </div>
        </div>

        <div class="description-section" *ngIf="false">
          <p class="description">{{ data.description }}</p>
        </div>

        <div class="detailed-report">
          <h3>{{ getDetailedReportTitle() }}</h3>
          <div class="report-content markdown-content" [innerHTML]="data.detailedReport | markdown"></div>
        </div>

        <div *ngIf="data.tableData" class="price-table-section">
          <h3>{{ getTableSectionTitle() }}</h3>
          <div class="table-view-wrapper">
            <query-process-result [queryProcessResult]="data.tableData" [showPinButton]="false"></query-process-result>
          </div>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ getCloseButtonText() }}</button>
      <button mat-button (click)="onReject()">
        <mat-icon>block</mat-icon>
        {{ getRejectButtonText() }}
      </button>
      <button mat-raised-button color="primary" (click)="onAccept()">
        <mat-icon>check_circle</mat-icon>
        {{ getAcceptButtonText() }}
      </button>
    </mat-dialog-actions>
  `,
  styleUrls: ['./insight-detail-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InsightDetailModalComponent implements AfterViewInit {
  constructor(
    public dialogRef: MatDialogRef<InsightDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InsightDetailData,
    private translate: IanTranslateService,
    private elementRef: ElementRef,
    private router: Router,
    private _fuseSidebarService: FuseSidebarService
  ) {
    _log('[InsightDetailModal] Data received:', this.data);
    _log('[InsightDetailModal] Table data:', this.data.tableData);
    if (this.data.tableData?.statement_response) {
      _log('[InsightDetailModal] Statement response:', this.data.tableData.statement_response);
      _log('[InsightDetailModal] Manifest:', this.data.tableData.statement_response.manifest);
      _log('[InsightDetailModal] Result:', this.data.tableData.statement_response.result);
      if (this.data.tableData.statement_response.result?.data_typed_array) {
        _log('[InsightDetailModal] Data array length:', this.data.tableData.statement_response.result.data_typed_array.length);
        _log('[InsightDetailModal] First row:', this.data.tableData.statement_response.result.data_typed_array[0]);
      }
    }
  }

  ngAfterViewInit(): void {
    // Reset scroll position to top when modal opens
    this.dialogRef.afterOpened().subscribe(() => {
      setTimeout(() => {
        // Try multiple selectors
        const scrollableContent =
          document.querySelector('.insight-detail-dialog mat-dialog-content') ||
          document.querySelector('.insight-detail-dialog .mat-dialog-content') ||
          document.querySelector('.insight-detail-dialog .mat-mdc-dialog-content') ||
          document.querySelector('mat-dialog-content.modal-content');

        if (scrollableContent) {
          scrollableContent.scrollTop = 0;
        } else {
          const allDialogContent = document.querySelectorAll('mat-dialog-content');

          if (allDialogContent.length > 0) {
            allDialogContent[allDialogContent.length - 1].scrollTop = 0;
          }
        }
      }, 0);
    });
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      const key = diffMins === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.MINUTES' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.MINUTES_PLURAL';
      return this.translate.instant(key).replace('{{count}}', diffMins.toString());
    } else if (diffHours < 24) {
      const key = diffHours === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.HOURS' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.HOURS_PLURAL';
      return this.translate.instant(key).replace('{{count}}', diffHours.toString());
    } else if (diffDays < 7) {
      const key = diffDays === 1 ? 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.DAYS' : 'INSIGHTS_SUGGESTIONS_PANEL.TIME_AGO.DAYS_PLURAL';
      return this.translate.instant(key).replace('{{count}}', diffDays.toString());
    } else {
      const lang = this.translate._translateService.currentLang || 'es';
      return date.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }

  getDetailedReportTitle(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.DETAILED_REPORT_TITLE');
  }

  getTypeLabel(type: InsightType): string {
    const key = `INSIGHTS_SUGGESTIONS_PANEL.TYPE.${type.toUpperCase()}`;
    return this.translate.instant(key) || type;
  }

  getPriorityLabel(priority: InsightPriority): string {
    const key = `INSIGHTS_SUGGESTIONS_PANEL.PRIORITY.${priority.toUpperCase()}`;
    return this.translate.instant(key) || priority;
  }

  formatCurrency(amount: number): string {
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `$ ${formatted}`;
  }

  getCloseButtonText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.CLOSE_BUTTON');
  }

  getAcceptButtonText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.ACCEPT_BUTTON');
  }

  getRejectButtonText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.REJECT_BUTTON');
  }

  getTableSectionTitle(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.PRICE_RECOMMENDATIONS_TABLE');
  }

  onAccept(): void {
    this.dialogRef.close({ action: 'accept' });
  }

  onReject(): void {
    this.dialogRef.close({ action: 'reject' });
  }

  onViewConversation(): void {
    _log('[InsightDetailModal] User requested to view chat for insight (keeping modal open):', this.data.insight_id);
    if (this.data.insight_id) {
      this.dialogRef.close();
      // Cerrar el panel de sugerencias si está abierto
      const sidebar = this._fuseSidebarService.getSidebar('insightsSuggestionsPanel');
      if (sidebar) {
        sidebar.close();
      }
      this.router.navigate(['/ai-chat/session', this.data.insight_id]);
    }
  }
  getViewConversationText(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.VIEW_CONVERSATION');
  }

  getViewConversationTooltip(): string {
    return this.translate.instant('INSIGHTS_SUGGESTIONS_PANEL.VIEW_CONVERSATION_TOOLTIP');
  }
}
